<template>
  <main class="wallet-transfer-select-coin-page">
    <AppNavBar :title="t('wallet.selectToken')" background-color="#f5f5f5" />

    <div class="token-search-wrap">
      <van-field
        v-model="searchText"
        class="token-search"
        :placeholder="t('wallet.enterContractAddressSearch')"
      >
        <template #left-icon>
          <app-image src="@/assets/images/search_icon.png" alt="" class="search-icon" />
        </template>
      </van-field>
    </div>

    <section class="token-list">
      <button
        v-for="item in filteredCoins"
        :key="item.contractAddress || item.symbol || item.coin"
        type="button"
        class="token-row"
        @click="selectCoin(item)"
      >
        <div class="token-row__identity">
          <app-image v-if="item.img || item.logo" :src="item.img || item.logo" alt="" class="token-icon" />
          <span v-else class="token-icon token-icon--fallback">{{ tokenInitial(item) }}</span>
          <div class="token-row__name">
            <span>{{ item.coin || item.symbol || '--' }}</span>
            <small>{{ item.contractAddress || '' }}</small>
          </div>
        </div>

        <div class="token-row__balance">
          <span>{{ formatTokenAmount(item.balance || 0) }}</span>
          <small>${{ formatMoney(assetUsdValue(item)) }}</small>
        </div>
      </button>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { useWalletTokenStore } from '@/stores/wallet-token';
import { useProfileStore } from '@/stores/profile';
import { findWithdrawCoin, useWalletWithdrawStore } from '@/stores/wallet-withdraw';
import { useWalletTransferFormStore } from '@/stores/wallet-transfer-form';
import { formatMoney, formatTokenAmount } from '@/utils/format';

const { t } = useI18n();
const router = useRouter();
const walletTokenStore = useWalletTokenStore();
const profileStore = useProfileStore();
const walletWithdrawStore = useWalletWithdrawStore();
const transferFormStore = useWalletTransferFormStore();

const searchText = ref('');
const coins = computed(() => walletTokenStore.assetList.map((item) => {
  const withdrawCoin = findWithdrawCoin(walletWithdrawStore.list, item);
  const balance = withdrawCoin?.balanceNormal || withdrawCoin?.balance || '0';
  return {
    ...item,
    balance: String(balance),
    balanceNormal: String(balance),
    balanceTo: Number(balance) * Number(item?.usdtPrice || 0)
  };
}));
const filteredCoins = computed(() => {
  const keyword = searchText.value.trim().toLowerCase();
  if (!keyword) return coins.value;

  return coins.value.filter((item) => [item.coin, item.symbol, item.contractAddress]
    .some((value) => String(value || '').toLowerCase().includes(keyword)));
});

function tokenInitial(item) {
  return String(item?.coin || item?.symbol || 'T').slice(0, 1).toUpperCase();
}

function assetUsdValue(item) {
  const listedValue = Number(item?.balanceTo || 0);
  if (listedValue > 0) return listedValue;
  return Number(item?.balance || 0) * Number(item?.usdtPrice || 0);
}

function selectCoin(item) {
  walletTokenStore.setCurrentAsset(item);
  router.back();
}

onBeforeRouteLeave((to) => {
  if (to.path !== '/wallet/transfer') {
    transferFormStore.clearDraft();
  }
});

onMounted(async () => {
  await Promise.all([
    walletTokenStore.fetchTransferCoinList({
      meta: { showLoading: false }
    }),
    walletWithdrawStore.fetchWithdrawInfo({
      address: profileStore.platformAddress || null,
      reset: !walletWithdrawStore.list.length
    }).catch(() => {})
  ]);
});
</script>

<style scoped lang="scss">
.wallet-transfer-select-coin-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.token-search-wrap {
  margin: 15px;
  padding: 0 15px;
  border-radius: 30px;
  background: #f0f0f0;
}

.token-search :deep(.van-cell) {
  padding: 10px 0;
  background: transparent;
}

.token-search :deep(.van-field__control) {
  color: #171515;
  font-size: 14px;
}

.token-search :deep(.van-field__control::placeholder) {
  color: #b1b1b1;
}

.search-icon {
  display: block;
  width: 18px;
  height: 18px;
  margin-right: 8px;
}

.token-list {
  max-height: calc(var(--app-viewport-height) - 110px);
  overflow-y: auto;
}

.token-row {
  display: flex;
  width: 100%;
  height: 60px;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  border: 0;
  background: #ffffff;
  color: #171515;
  text-align: left;
}

.token-row__identity {
  display: flex;
  min-width: 0;
  align-items: center;
}

.token-icon {
  display: block;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 50%;
  object-fit: cover;
}

.token-icon--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2667ff;
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
}

.token-row__name {
  display: flex;
  min-width: 0;
  flex-direction: column;
  margin-left: 20px;
  gap: 3px;

  span {
    font-size: 14px;
  }

  small {
    overflow: hidden;
    color: #444444;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.token-row__balance {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;

  span {
    font-size: 14px;
  }

  small {
    color: #444444;
    font-size: 12px;
  }
}
</style>
